/**
 * Page /grc/factures — Pointage des règlements (régie / comptabilité GRC).
 *
 * Destinée à l'agent comptable / manager GRC : visualiser TOUTES les factures
 * (cantine, périscolaire, et services à venir) et pointer les règlements reçus
 * hors ligne — chèque, CB en régie, espèces, virement — comme une feuille de
 * pointage. Le paiement en ligne PayFiP, lui, se solde tout seul ; cet écran
 * couvre les encaissements manuels.
 *
 * Permission serveur : grc.facture.read (voir) / grc.facture.encaisser (pointer).
 */

import { useEffect, useState, useCallback } from 'react';
import { api, ApiException } from '@/lib/api';
import { Card, Button, Badge, Alert, LogoMark, SectionEyebrow, Field, Input } from '@/components/ui';
import { GrcLayout } from '@/components/grc/GrcLayout';
import {
  Receipt, Euro, CheckCircle2, AlertCircle, Inbox, Banknote, FileDown, Mail,
} from 'lucide-react';
import type { Facture, FactureStatut, ServiceFacturable, MoyenEncaissement } from '@roronoa-collectivites/shared-types';

const euros = (c: number) => (c / 100).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const SERVICE_LABEL: Record<string, string> = { cantine: 'Cantine', periscolaire: 'Périscolaire' };
const STATUT_VARIANT: Record<FactureStatut, 'info' | 'success' | 'warning' | 'urgent'> = {
  emise: 'info', envoyee: 'info', partiellement_payee: 'warning', payee: 'success', impayee: 'urgent', annulee: 'warning',
};
const STATUT_LABEL: Record<FactureStatut, string> = {
  emise: 'À encaisser', envoyee: 'À encaisser', partiellement_payee: 'Partiel', payee: 'Payée', impayee: 'Impayée', annulee: 'Annulée',
};

const MOYENS: { value: MoyenEncaissement; label: string }[] = [
  { value: 'cheque', label: 'Chèque' },
  { value: 'cb_payfip', label: 'Carte bancaire' },
  { value: 'espece', label: 'Espèces' },
  { value: 'virement', label: 'Virement' },
  { value: 'sepa', label: 'Prélèvement SEPA' },
];

type Etat = 'attente' | 'payee' | 'annulee' | 'toutes';
const ETATS: { key: Etat; label: string }[] = [
  { key: 'attente', label: 'À encaisser' },
  { key: 'payee', label: 'Payées' },
  { key: 'annulee', label: 'Annulées' },
  { key: 'toutes', label: 'Toutes' },
];

export function GrcPointageFacturesPage() {
  const [factures, setFactures] = useState<Facture[]>([]);
  const [resume, setResume] = useState<{ nbEnAttente: number; montantEnAttente: number }>({ nbEnAttente: 0, montantEnAttente: 0 });
  const [etat, setEtat] = useState<Etat>('attente');
  const [service, setService] = useState<ServiceFacturable | ''>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);

  // Pointage en cours (ligne dépliée)
  const [pointageId, setPointageId] = useState<string | null>(null);
  const [moyen, setMoyen] = useState<MoyenEncaissement>('cheque');
  const [reference, setReference] = useState('');
  const [saving, setSaving] = useState(false);

  // Détail d'une facture payée (encaissements) + édition du moyen
  const [detailId, setDetailId] = useState<string | null>(null);
  const [detail, setDetail] = useState<Facture | null>(null);
  const [editEncId, setEditEncId] = useState<string | null>(null);
  const [editMoyen, setEditMoyen] = useState<MoyenEncaissement>('cheque');
  const [editRef, setEditRef] = useState('');
  const [exporting, setExporting] = useState(false);
  const [envoiEmailId, setEnvoiEmailId] = useState<string | null>(null);
  const [emailMsg, setEmailMsg] = useState<{ id: string; ok: boolean; text: string } | null>(null);

  async function voirDetail(f: Facture) {
    if (detailId === f.id) { setDetailId(null); setDetail(null); return; }
    setDetailId(f.id); setDetail(null); setEditEncId(null);
    try { setDetail(await api.grc.factures.get(f.id)); }
    catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur de chargement'); }
  }

  function ouvrirEditEnc(encId: string, m: MoyenEncaissement, ref: string | null) {
    setEditEncId(encId); setEditMoyen(m); setEditRef(ref ?? '');
  }

  async function enregistrerEditEnc(factureId: string) {
    if (!editEncId) return;
    setSaving(true); setError(null);
    try {
      const maj = await api.grc.factures.modifierEncaissement(factureId, editEncId, { moyen: editMoyen, reference: editRef.trim() || null });
      setDetail(maj); setEditEncId(null); setOk('Mode de paiement mis à jour.');
      setTimeout(() => setOk(null), 3000);
    } catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur'); }
    finally { setSaving(false); }
  }

  async function telechargerExcel() {
    setExporting(true); setError(null);
    try {
      const blob = await api.grc.factures.exportExcelBlob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `factures-${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { setError(e instanceof ApiException ? e.message : 'Export impossible'); }
    finally { setExporting(false); }
  }

  async function envoyerEmail(factureId: string) {
    setEnvoiEmailId(factureId); setEmailMsg(null); setError(null);
    try {
      const r = await api.scolaire.facturation.envoyerEmail(factureId);
      setEmailMsg(r.sent
        ? { id: factureId, ok: true, text: 'Facture envoyée par e-mail au responsable.' }
        : { id: factureId, ok: false, text: r.reason === 'aucun responsable avec e-mail' ? 'Aucun responsable avec adresse e-mail pour cette famille.' : `Envoi impossible${r.reason ? ` : ${r.reason}` : ''}.` });
    } catch (e) { setError(e instanceof ApiException ? e.message : 'Envoi impossible'); }
    finally { setEnvoiEmailId(null); }
  }

  const charger = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const r = await api.grc.factures.list({ etat, service: service || undefined });
      setFactures(r.factures); setResume(r.resume);
    } catch (e) {
      setError(e instanceof ApiException ? e.message : 'Erreur de chargement');
    } finally { setLoading(false); }
  }, [etat, service]);

  useEffect(() => { void charger(); }, [charger]);

  function ouvrirPointage(f: Facture) {
    setPointageId(f.id); setMoyen('cheque'); setReference(''); setOk(null); setError(null);
  }

  async function valider(f: Facture) {
    if (!reference.trim()) { setError('La référence du règlement est obligatoire.'); return; }
    setSaving(true); setError(null);
    try {
      await api.grc.factures.pointer(f.id, { moyen, reference: reference.trim() });
      setOk(`Facture ${f.numero} pointée (${euros(f.montantTtc)} €).`);
      setPointageId(null);
      await charger();
    } catch (e) {
      setError(e instanceof ApiException ? e.message : 'Erreur');
    } finally { setSaving(false); }
  }

  return (
    <GrcLayout active="factures">
      <main id="main-content" className="mx-auto max-w-4xl px-4 py-8 sm:px-6 sm:py-10 animate-fade-in">
        <div className="mb-6 flex items-center gap-3">
          <LogoMark icon={Receipt} accent="grc" size="lg" />
          <div>
            <SectionEyebrow>Régie · comptabilité</SectionEyebrow>
            <h1 className="font-display text-xl font-semibold text-ink sm:text-2xl">Pointage des factures</h1>
          </div>
        </div>

        {/* Récap */}
        <Card className="mb-5">
          <div className="flex flex-wrap items-center gap-x-8 gap-y-2 p-4">
            <div>
              <div className="text-2xs uppercase tracking-wide text-ink-subtle">À encaisser</div>
              <div className="font-display text-2xl font-semibold text-ink">{resume.nbEnAttente}</div>
            </div>
            <div>
              <div className="text-2xs uppercase tracking-wide text-ink-subtle">Montant en attente</div>
              <div className="font-display text-2xl font-semibold text-ink tabular-nums">{euros(resume.montantEnAttente)} €</div>
            </div>
          </div>
        </Card>

        {error && <Alert variant="error" icon={AlertCircle} className="mb-4">{error}</Alert>}
        {ok && <Alert variant="success" icon={CheckCircle2} className="mb-4">{ok}</Alert>}

        {/* Filtres */}
        <div className="mb-4 flex flex-wrap items-center gap-2">
          {ETATS.map((e) => (
            <button key={e.key} type="button" onClick={() => setEtat(e.key)}
              className={['rounded-r-md border px-3 py-1.5 text-sm font-medium transition-colors',
                etat === e.key ? 'border-grc bg-grc/10 text-grc-fg' : 'border-border text-ink-muted hover:text-ink'].join(' ')}>
              {e.label}
            </button>
          ))}
          <span className="mx-1 h-5 w-px bg-border" aria-hidden="true" />
          <select value={service} onChange={(ev) => setService(ev.target.value as ServiceFacturable | '')}
            className="rounded-r-md border border-border bg-surface px-3 py-1.5 text-sm text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-grc">
            <option value="">Tous services</option>
            <option value="cantine">Cantine</option>
            <option value="periscolaire">Périscolaire</option>
            <option value="alsh">ALSH</option>
          </select>
          <div className="ml-auto">
            <Button variant="secondary" size="sm" loading={exporting} onClick={() => void telechargerExcel()}>
              <FileDown className="h-4 w-4" /> Export Excel
            </Button>
          </div>
        </div>

        {/* Liste */}
        {loading ? (
          <p className="text-sm text-ink-muted">Chargement…</p>
        ) : factures.length === 0 ? (
          <Card className="p-8 text-center">
            <Inbox className="mx-auto mb-3 h-10 w-10 text-ink-subtle" />
            <p className="text-sm text-ink-muted">Aucune facture dans cette vue.</p>
          </Card>
        ) : (
          <div className="space-y-2">
            {factures.map((f) => (
              <Card key={f.id} className="overflow-hidden">
                <div className="flex flex-wrap items-center gap-3 p-4">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-2xs text-ink-muted">{f.numero}</span>
                      <Badge variant="neutral">{SERVICE_LABEL[f.service] ?? f.service}</Badge>
                      <Badge variant={STATUT_VARIANT[f.statut]}>{STATUT_LABEL[f.statut]}</Badge>
                    </div>
                    <div className="mt-0.5 text-sm text-ink">{f.familleNom ?? '—'}</div>
                    <div className="text-2xs text-ink-subtle tabular-nums">{f.periodeDebut} → {f.periodeFin}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-display text-lg font-semibold text-ink tabular-nums">{euros(f.montantTtc)} €</div>
                  </div>
                  {f.statut !== 'payee' && f.statut !== 'annulee' && (
                    <Button variant="grc" size="sm" onClick={() => (pointageId === f.id ? setPointageId(null) : ouvrirPointage(f))}>
                      <Banknote className="h-4 w-4" /> {pointageId === f.id ? 'Fermer' : 'Pointer'}
                    </Button>
                  )}
                  {(f.statut === 'payee' || f.statut === 'partiellement_payee') && (
                    <Button variant="secondary" size="sm" onClick={() => voirDetail(f)}>
                      <Receipt className="h-4 w-4" /> {detailId === f.id ? 'Fermer' : 'Détails'}
                    </Button>
                  )}
                  {f.statut !== 'annulee' && (
                    <Button variant="secondary" size="sm" loading={envoiEmailId === f.id} onClick={() => void envoyerEmail(f.id)}>
                      <Mail className="h-4 w-4" /> E-mail
                    </Button>
                  )}
                </div>
                {emailMsg && emailMsg.id === f.id && (
                  <Alert variant={emailMsg.ok ? 'success' : 'warning'} className="mt-2">{emailMsg.text}</Alert>
                )}

                {/* Détail des règlements (factures payées / partielles) */}
                {detailId === f.id && (
                  <div className="border-t border-border bg-surface/40 p-4">
                    {!detail ? (
                      <p className="text-sm text-ink-muted">Chargement…</p>
                    ) : (detail.encaissements ?? []).length === 0 ? (
                      <p className="text-sm text-ink-muted">Aucun règlement enregistré.</p>
                    ) : (
                      <div className="space-y-2">
                        <p className="text-xs font-semibold uppercase tracking-wide text-ink-subtle">Règlements</p>
                        {(detail.encaissements ?? []).map((e) => (
                          <div key={e.id} className="rounded-r-md border border-border bg-surface p-3">
                            {editEncId === e.id ? (
                              <div className="grid gap-3 sm:grid-cols-2">
                                <Field label="Moyen de paiement" htmlFor={`em-${e.id}`}>
                                  <select id={`em-${e.id}`} value={editMoyen} onChange={(ev) => setEditMoyen(ev.target.value as MoyenEncaissement)}
                                    className="w-full rounded-r-md border border-border bg-surface px-3 py-2 text-sm text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-grc">
                                    {MOYENS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
                                  </select>
                                </Field>
                                <Field label="Référence" htmlFor={`er-${e.id}`}>
                                  <Input id={`er-${e.id}`} value={editRef} onChange={(ev) => setEditRef(ev.target.value)} placeholder="N° chèque, réf…" />
                                </Field>
                                <div className="sm:col-span-2 flex justify-end gap-2">
                                  <Button variant="secondary" size="sm" onClick={() => setEditEncId(null)}>Annuler</Button>
                                  <Button variant="grc" size="sm" loading={saving} onClick={() => void enregistrerEditEnc(f.id)}>Enregistrer</Button>
                                </div>
                              </div>
                            ) : (
                              <div className="flex flex-wrap items-center justify-between gap-2">
                                <div className="text-sm text-ink">
                                  <span className="font-medium">{MOYENS.find((m) => m.value === e.moyen)?.label ?? e.moyen}</span>
                                  <span className="text-ink-muted"> · {euros(e.montant)} €</span>
                                  {e.reference && <span className="text-ink-subtle"> · réf. {e.reference}</span>}
                                  {e.dateEncaissement && <span className="text-2xs text-ink-subtle"> · {String(e.dateEncaissement).slice(0, 10)}</span>}
                                </div>
                                <Button variant="ghost" size="sm" onClick={() => ouvrirEditEnc(e.id, e.moyen as MoyenEncaissement, e.reference)}>
                                  Modifier le mode
                                </Button>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Bloc de pointage déplié */}
                {pointageId === f.id && (
                  <div className="border-t border-border bg-surface/40 p-4">
                    <div className="grid gap-3 sm:grid-cols-2">
                      <Field label="Moyen de paiement" htmlFor={`m-${f.id}`}>
                        <select id={`m-${f.id}`} value={moyen} onChange={(e) => setMoyen(e.target.value as MoyenEncaissement)}
                          className="w-full rounded-r-md border border-border bg-surface px-3 py-2 text-sm text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-grc">
                          {MOYENS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
                        </select>
                      </Field>
                      <Field label="Référence (obligatoire)" htmlFor={`r-${f.id}`}>
                        <Input id={`r-${f.id}`} value={reference} onChange={(e) => setReference(e.target.value)}
                          placeholder="N° chèque, ticket régie, réf. virement…" />
                      </Field>
                    </div>
                    <div className="mt-3 flex justify-end gap-2">
                      <Button variant="secondary" size="sm" onClick={() => setPointageId(null)}>Annuler</Button>
                      <Button variant="grc" size="sm" loading={saving} onClick={() => void valider(f)}>
                        <CheckCircle2 className="h-4 w-4" /> Marquer payée
                      </Button>
                    </div>
                  </div>
                )}
              </Card>
            ))}
          </div>
        )}
      </main>
    </GrcLayout>
  );
}
